import { useMemo } from 'react';
import {
  getDashboardSummary,
  getDashboardTimeline,
  type DashboardSummary,
  type TimelineEvent,
} from '@/api/dashboard';
import {
  mockDashboardSummary,
  mockDashboardTimeline,
} from '@/data/mock-dashboard-data';
import { useApiQuery } from './useApiQuery';

interface UseDashboardResult {
  summary: DashboardSummary | undefined;
  timeline: TimelineEvent[];
  isLoading: boolean;
  error: string | undefined;
  isUsingMockData: boolean;
  reload: () => void;
}

export const useDashboard = (months = 6): UseDashboardResult => {
  const summaryQuery = useApiQuery<DashboardSummary>({
    queryFn: () => getDashboardSummary(),
    mockData: mockDashboardSummary,
  });

  const timelineQuery = useApiQuery<TimelineEvent[]>(
    {
      queryFn: () => getDashboardTimeline(months),
      mockData: mockDashboardTimeline,
    },
    [months],
  );

  const isLoading = summaryQuery.isLoading || timelineQuery.isLoading;
  const error = summaryQuery.error ?? timelineQuery.error;
  const isUsingMockData =
    summaryQuery.isUsingMockData || timelineQuery.isUsingMockData;

  const reload = useMemo(() => {
    return () => {
      summaryQuery.reload();
      timelineQuery.reload();
    };
  }, [summaryQuery.reload, timelineQuery.reload]);

  return {
    summary: summaryQuery.data,
    timeline: timelineQuery.data ?? [],
    isLoading,
    error,
    isUsingMockData,
    reload,
  };
};
